
import { ServiceData } from '@/types/serviceTypes';

// Extended service data with SEO and location content
export interface EnhancedServiceData extends ServiceData {
  metaTitle: string;
  metaDescription: string;
  slug: string;
  heroHeading: string;
  localContent: {
    intro: string;
    regionalBenefits: string[];
    seasonalTips: string[];
  };
  faqs: Array<{ question: string; answer: string }>;
  targetTowns: string[]; // Town slugs for internal linking
  commercialAvailable: boolean;
}

export const enhancedLawnMaintenanceService: EnhancedServiceData = {
  title: 'Lawn Maintenance',
  slug: 'lawn-maintenance',
  metaTitle: 'Lawn Maintenance Services Fairfield County CT | Weekly Lawn Care',
  metaDescription: 'Professional lawn maintenance in Fairfield & New Haven County, CT. Weekly mowing, edging, fertilization & seasonal care. Serving Westport, Darien, Fairfield & more.',
  heroHeading: 'Professional Lawn Maintenance in Connecticut',
  description: 'Comprehensive lawn care services to keep your grass healthy and vibrant year-round across Fairfield and New Haven Counties.',
  longDescription: [
    'Our professional lawn maintenance services are designed to keep your property looking its best throughout the growing season. We take a holistic approach to lawn care, focusing not just on regular mowing but on the overall health and appearance of your turf.',
    'Serving homes and businesses across Fairfield County and New Haven County, our trained technicians evaluate your lawn\'s specific needs and develop a customized maintenance plan. From proper mowing height and frequency to edge trimming and debris removal, we handle every detail with care and precision.',
    'Connecticut\'s unique climate presents specific challenges for lawns—from humid summers to cold winters. Our team understands the local conditions in Westport, Darien, Fairfield, Trumbull, and surrounding towns, adapting our approach to ensure your lawn thrives in every season.'
  ],
  localContent: {
    intro: 'From the coastal lawns of Westport and Fairfield to the wooded properties of Weston and Easton, Connecticut lawns face unique challenges. Salt exposure near the coast, heavy shade from mature trees inland, and Connecticut\'s variable weather all require expert care.',
    regionalBenefits: [
      'Local expertise with Connecticut grass varieties and soil conditions',
      'Understanding of coastal vs. inland lawn care needs',
      'Seasonal timing adjusted for Connecticut\'s climate',
      'Experience with high-value property standards in Fairfield County'
    ],
    seasonalTips: [
      'Spring: Early cleanup, aeration, and overseeding to repair winter damage',
      'Summer: Proper mowing height (3-4 inches) to protect roots during heat',
      'Fall: Leaf management and final fertilization before dormancy',
      'Winter: Equipment winterization and dormant lawn protection'
    ]
  },
  image: 'https://images.unsplash.com/photo-1589923188900-85dae523342b?q=80&w=2070&auto=format&fit=crop',
  features: [
    'Weekly or bi-weekly mowing schedules',
    'Precision edge trimming along walkways and beds',
    'Blowing and cleanup of clippings and debris',
    'String trimming around obstacles and tight spaces',
    'Seasonal adjustments to mowing heights',
    'Property-specific care plans'
  ],
  benefits: [
    'Enhanced curb appeal and property value',
    'Healthier turf with fewer weeds and bare spots',
    'Consistent professional appearance',
    'More time to enjoy your outdoor space',
    'Prevention of common lawn problems'
  ],
  process: [
    { title: 'Initial Assessment', description: 'We evaluate your lawn\'s condition, grass type, sun exposure, and specific challenges.' },
    { title: 'Customized Plan', description: 'Based on the assessment, we create a maintenance schedule tailored to your lawn\'s needs.' },
    { title: 'Regular Service', description: 'Our trained technicians perform scheduled maintenance following best practices.' },
    { title: 'Ongoing Monitoring', description: 'We continuously assess your lawn\'s health and adjust our approach as needed.' }
  ],
  relatedServices: [
    { id: 'fertilization', title: 'Fertilization' },
    { id: 'spring-fall-cleanups', title: 'Spring/Fall Clean Ups' },
    { id: 'irrigation', title: 'Irrigation' }
  ],
  faqs: [
    { question: 'How often should my lawn be mowed in Connecticut?', answer: 'During the peak growing season (May through October), we recommend weekly mowing. In spring and fall, bi-weekly may be sufficient depending on growth rates and weather conditions.' },
    { question: 'What is the proper mowing height for Connecticut lawns?', answer: 'For most Connecticut lawns with cool-season grasses, we maintain a height of 3-4 inches. This promotes deeper root growth and helps the lawn withstand summer heat and drought.' },
    { question: 'Do you offer lawn maintenance contracts?', answer: 'Yes, we offer seasonal and annual maintenance contracts with consistent scheduling and predictable pricing. Contracts include priority scheduling and bundled service discounts.' },
    { question: 'What areas do you serve for lawn maintenance?', answer: 'We serve Fairfield County including Westport, Darien, Fairfield, Norwalk, Trumbull, Monroe, Easton, and Weston, plus New Haven County towns like Milford, Orange, and Woodbridge.' },
    { question: 'Is lawn maintenance included in commercial property contracts?', answer: 'Yes, lawn maintenance is a core component of our commercial property maintenance contracts. We serve office parks, HOAs, retail centers, and other commercial properties.' },
    { question: 'Do you handle coastal lawn care differently?', answer: 'Absolutely. Coastal properties in Westport, Fairfield, and Norwalk require different approaches including salt-tolerant grass varieties and adjusted irrigation to handle salt exposure.' }
  ],
  targetTowns: ['westport-ct', 'darien-ct', 'fairfield-ct', 'norwalk-ct', 'trumbull-ct', 'easton-ct', 'weston-ct', 'monroe-ct'],
  commercialAvailable: true
};

export const enhancedLandscapeDesignService: EnhancedServiceData = {
  title: 'Landscape Design',
  slug: 'landscape-design',
  metaTitle: 'Landscape Design Services CT | Custom Garden & Property Design',
  metaDescription: 'Expert landscape design in Fairfield County, CT. Custom residential & estate designs, 3D visualization, native plantings. Serving Westport, Darien, Fairfield & beyond.',
  heroHeading: 'Custom Landscape Design for Connecticut Properties',
  description: 'Transform your outdoor space with professional landscape design tailored to Connecticut\'s climate and your property\'s unique characteristics.',
  longDescription: [
    'Our landscape design services blend artistic vision with horticultural expertise to create outdoor spaces that enhance your lifestyle and property value. We specialize in designs that complement Connecticut\'s natural beauty while addressing practical needs like drainage, privacy, and maintenance.',
    'Whether you\'re envisioning a serene garden retreat in Weston, a sophisticated estate landscape in Darien, or a coastal-inspired design in Fairfield, our designers work closely with you to bring your vision to life. We use 3D visualization technology so you can see your new landscape before a single plant goes in the ground.',
    'Every design considers Connecticut\'s four-season climate, soil conditions, and local ecosystem. We emphasize native and adapted plants that thrive in our region, creating landscapes that are beautiful, sustainable, and easier to maintain.'
  ],
  localContent: {
    intro: 'Connecticut\'s diverse landscapes—from Long Island Sound shorelines to the rolling hills of the interior—inspire our design approach. We create gardens and landscapes that belong here, using plants and materials that complement the regional character.',
    regionalBenefits: [
      'Deep knowledge of Connecticut-native plants and ecosystems',
      'Experience designing for coastal, suburban, and wooded properties',
      'Understanding of local zoning and wetlands regulations',
      'Relationships with premier Connecticut nurseries and suppliers'
    ],
    seasonalTips: [
      'Spring: Best time for major planting projects as roots establish before summer heat',
      'Summer: Focus on hardscape installation while plants are most visible for design decisions',
      'Fall: Ideal for tree planting and bulb installation for spring color',
      'Winter: Perfect time for design consultations and project planning'
    ]
  },
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2070&auto=format&fit=crop',
  features: [
    'Comprehensive site analysis and assessment',
    '3D design visualization and renderings',
    'Native and adapted plant selection',
    'Sustainable design principles',
    'Phased implementation planning',
    'Coordination with architects and builders'
  ],
  benefits: [
    'Increased property value and curb appeal',
    'Personalized outdoor living spaces',
    'Lower long-term maintenance costs',
    'Environmentally responsible landscaping',
    'Four-season visual interest'
  ],
  process: [
    { title: 'Initial Consultation', description: 'We discuss your vision, lifestyle needs, budget, and timeline in an on-site meeting.' },
    { title: 'Site Analysis', description: 'Detailed evaluation of soil, drainage, sun exposure, views, and existing features.' },
    { title: 'Design Development', description: 'Creation of concept designs with plant palettes, hardscape elements, and 3D visualizations.' },
    { title: 'Final Design & Installation', description: 'Refined plans and professional installation by our experienced crews.' }
  ],
  relatedServices: [
    { id: 'hardscape-projects', title: 'Hardscape Projects' },
    { id: 'plant-design', title: 'Plant Design' },
    { id: 'irrigation', title: 'Irrigation' }
  ],
  faqs: [
    { question: 'How long does landscape design take?', answer: 'The design phase typically takes 2-4 weeks depending on project complexity. Simple garden designs may be faster, while comprehensive estate landscapes require more time for thorough planning.' },
    { question: 'Do you provide 3D renderings of designs?', answer: 'Yes, we provide 3D visualizations for all significant design projects. This helps you see exactly how your new landscape will look before installation begins.' },
    { question: 'Can you work with my architect or builder?', answer: 'Absolutely. We regularly coordinate with architects, builders, and other professionals to ensure seamless integration of landscape design with new construction or renovation projects.' },
    { question: 'Do you use native plants in your designs?', answer: 'Native plants are a cornerstone of our design philosophy. They\'re adapted to Connecticut\'s climate, support local wildlife, and typically require less maintenance and water.' },
    { question: 'What is the cost of landscape design services?', answer: 'Design fees vary based on property size and project scope. We provide detailed proposals after initial consultations. Design fees are typically applied toward installation if we complete the project.' },
    { question: 'Can you design for deer-resistant landscapes?', answer: 'Yes, deer resistance is a common request in towns like Weston, Easton, and Woodbridge. We know which plants deer avoid and design beautiful gardens that minimize browse damage.' }
  ],
  targetTowns: ['westport-ct', 'darien-ct', 'weston-ct', 'easton-ct', 'fairfield-ct', 'woodbridge-ct', 'orange-ct', 'monroe-ct'],
  commercialAvailable: true
};

export const enhancedHardscapeService: EnhancedServiceData = {
  title: 'Hardscape Projects',
  slug: 'hardscaping',
  metaTitle: 'Hardscaping Services CT | Patios, Walkways & Retaining Walls',
  metaDescription: 'Professional hardscaping in Fairfield County, CT. Custom patios, walkways, retaining walls & outdoor living spaces. Quality stonework serving Westport to Shelton.',
  heroHeading: 'Premium Hardscaping for Connecticut Properties',
  description: 'Create lasting outdoor living spaces with expert hardscape design and installation—patios, walkways, walls, and more.',
  longDescription: [
    'Hardscaping transforms your property with permanent structures that extend your living space outdoors. From elegant bluestone patios to sturdy retaining walls, our skilled craftsmen deliver quality stonework that withstands Connecticut\'s freeze-thaw cycles and enhances your property for decades.',
    'We work with premium materials including natural stone, pavers, and concrete to create functional and beautiful outdoor features. Our team handles projects of all sizes—from intimate courtyard patios in Darien to expansive outdoor living areas with kitchens and fire features in Westport estates.',
    'Every hardscape project begins with proper site preparation, including drainage solutions and stable base construction. This attention to foundation work ensures your investment remains level, stable, and beautiful through Connecticut\'s challenging weather.'
  ],
  localContent: {
    intro: 'Connecticut\'s climate demands hardscaping built to last. Our installations are engineered for freeze-thaw durability, proper drainage, and long-term performance in New England conditions.',
    regionalBenefits: [
      'Materials selected for Connecticut freeze-thaw performance',
      'Proper drainage engineering for our wet climate',
      'Experience with Connecticut building codes and permits',
      'Local stone sourcing for authentic New England aesthetics'
    ],
    seasonalTips: [
      'Spring: Ground settling allows for optimal base preparation',
      'Summer: Prime construction season with stable weather',
      'Fall: Excellent conditions before ground freezes',
      'Winter: Planning and design for spring construction'
    ]
  },
  image: 'https://images.unsplash.com/photo-1617720356637-6264c1c0b4bb?q=80&w=2070&auto=format&fit=crop',
  features: [
    'Patio design and installation',
    'Walkways and garden paths',
    'Retaining walls and seat walls',
    'Outdoor kitchens and fire features',
    'Driveway aprons and parking areas',
    'Pool surrounds and decking'
  ],
  benefits: [
    'Expanded outdoor living space',
    'Increased property value',
    'Low maintenance outdoor surfaces',
    'Improved drainage and erosion control',
    'Year-round usability with proper features'
  ],
  process: [
    { title: 'Design Consultation', description: 'We discuss your vision, evaluate the site, and explore material options.' },
    { title: 'Detailed Proposal', description: 'Complete scope of work, material specifications, timeline, and investment.' },
    { title: 'Site Preparation', description: 'Excavation, grading, drainage installation, and base construction.' },
    { title: 'Installation & Finishing', description: 'Expert material installation, jointing, sealing, and final cleanup.' }
  ],
  relatedServices: [
    { id: 'landscape-design', title: 'Landscape Design' },
    { id: 'masonry', title: 'Masonry' },
    { id: 'custom-stonework', title: 'Custom Stonework' }
  ],
  faqs: [
    { question: 'What materials do you use for patios in Connecticut?', answer: 'We commonly use bluestone, granite, limestone, and premium concrete pavers. Material selection depends on style preferences, budget, and how the space will be used. We help you choose the best option for your project.' },
    { question: 'How long does a patio installation take?', answer: 'A typical residential patio takes 1-2 weeks from excavation to completion. Larger projects with multiple features may take longer. Weather can affect timelines during wet periods.' },
    { question: 'Do you handle drainage during hardscape installation?', answer: 'Absolutely. Proper drainage is critical in Connecticut. We engineer drainage solutions into every project to prevent water pooling and protect your investment from freeze-thaw damage.' },
    { question: 'Can you build outdoor kitchens?', answer: 'Yes, outdoor kitchens are a specialty. We design and build complete outdoor cooking areas with counters, built-in grills, refrigeration, and more. These are popular for Westport and Darien properties.' },
    { question: 'What about permits for hardscape projects?', answer: 'We handle permit requirements where applicable. Connecticut towns have varying regulations for hardscape work, and we know the requirements in each community we serve.' },
    { question: 'Do you offer retaining wall construction?', answer: 'Yes, retaining walls are essential for many Connecticut properties with grade changes. We build structural walls engineered for stability and aesthetic walls for garden features.' }
  ],
  targetTowns: ['westport-ct', 'darien-ct', 'fairfield-ct', 'norwalk-ct', 'trumbull-ct', 'shelton-ct', 'stratford-ct', 'milford-ct'],
  commercialAvailable: true
};

export const enhancedSnowRemovalService: EnhancedServiceData = {
  title: 'Snow Removal',
  slug: 'snow-removal',
  metaTitle: 'Snow Removal Services CT | Commercial & Residential Plowing',
  metaDescription: 'Reliable snow removal in Fairfield County, CT. Commercial plowing, residential driveways, ice management & 24/7 service. Serving Greater Bridgeport businesses.',
  heroHeading: 'Dependable Snow Removal in Connecticut',
  description: 'Keep your property safe and accessible with professional snow removal and ice management throughout Fairfield and New Haven Counties.',
  longDescription: [
    'Connecticut winters demand reliable snow removal you can count on. PK Landscape Associates provides comprehensive winter services for residential and commercial properties throughout Fairfield and New Haven Counties, with 24/7 monitoring and rapid response when storms hit.',
    'Our commercial clients—from office parks in Shelton to retail centers in Norwalk—trust us for priority service that keeps their properties safe and accessible. We provide liability documentation, consistent service, and communication that property managers and business owners require.',
    'Residential clients appreciate our attention to detail, from careful driveway plowing that protects surfaces to thorough walkway clearing and ice treatment. We treat your property with the same care we\'d give our own.'
  ],
  localContent: {
    intro: 'From nor\'easters to lake-effect snow bands, Connecticut winters are unpredictable. Our team monitors weather 24/7 and responds quickly to keep your property safe and accessible.',
    regionalBenefits: [
      '24/7 weather monitoring during winter season',
      'Strategic equipment positioning across service area',
      'Priority commercial response agreements',
      'De-icing materials optimized for Connecticut conditions'
    ],
    seasonalTips: [
      'November: Pre-season contracts and equipment preparation',
      'December-February: Peak storm response with full crews',
      'March: Late-season storms and transition to spring',
      'Year-round: Equipment maintenance and planning'
    ]
  },
  image: 'https://images.unsplash.com/photo-1491002052546-bf38f186af56?q=80&w=2070&auto=format&fit=crop',
  features: [
    'Commercial plowing with priority response',
    'Residential driveway and walkway clearing',
    'Salt and sand application',
    'Ice management and prevention',
    '24/7 weather monitoring',
    'Liability documentation for commercial clients'
  ],
  benefits: [
    'Safe access during and after storms',
    'Reduced slip-and-fall liability',
    'Consistent, reliable service',
    'Professional communication',
    'Peace of mind during winter storms'
  ],
  process: [
    { title: 'Pre-Season Setup', description: 'Site evaluation, trigger depth agreements, and emergency contact protocols.' },
    { title: 'Weather Monitoring', description: '24/7 monitoring of forecasts and real-time conditions throughout winter.' },
    { title: 'Storm Response', description: 'Crews mobilize at agreed trigger depths with systematic property coverage.' },
    { title: 'Post-Storm Follow-Up', description: 'Ice treatment, cleanup, and communication on service completion.' }
  ],
  relatedServices: [
    { id: 'lawn-maintenance', title: 'Lawn Maintenance' },
    { id: 'spring-fall-cleanups', title: 'Spring/Fall Clean Ups' }
  ],
  faqs: [
    { question: 'What is a trigger depth for snow removal?', answer: 'A trigger depth is the snow accumulation at which we begin service. Common triggers are 2 inches for commercial and 3-4 inches for residential. This is established in your service agreement.' },
    { question: 'Do you offer commercial snow removal contracts?', answer: 'Yes, commercial contracts are our specialty. We offer seasonal contracts with predictable pricing, priority response, and detailed documentation for liability protection.' },
    { question: 'What areas do you serve for snow removal?', answer: 'We serve commercial and residential properties throughout Fairfield and New Haven Counties, with concentrated coverage in Bridgeport, Shelton, Stratford, Norwalk, and surrounding towns.' },
    { question: 'Do you provide salt and sand application?', answer: 'Yes, de-icing is a core part of our winter services. We use appropriate materials for each situation—rock salt, treated salt, sand, or ice melt—based on temperatures and surfaces.' },
    { question: 'How do you handle overnight storms?', answer: 'We monitor weather 24/7. For overnight storms, crews mobilize as needed to ensure properties are cleared before morning business hours or commute times.' },
    { question: 'Is residential snow removal available?', answer: 'Yes, we offer residential plowing and walkway clearing. Service is available as part of year-round maintenance contracts or as standalone winter service.' }
  ],
  targetTowns: ['bridgeport-ct', 'shelton-ct', 'stratford-ct', 'norwalk-ct', 'trumbull-ct', 'fairfield-ct', 'milford-ct', 'west-haven-ct'],
  commercialAvailable: true
};

export const enhancedIrrigationService: EnhancedServiceData = {
  title: 'Irrigation Services',
  slug: 'irrigation',
  metaTitle: 'Irrigation Services CT | Sprinkler Installation & Repair | Fairfield County',
  metaDescription: 'Professional irrigation services in Connecticut. Sprinkler system installation, repair, winterization & smart controllers. Serving Westport, Darien, Fairfield & more.',
  heroHeading: 'Smart Irrigation Solutions for Connecticut Properties',
  description: 'Water-efficient irrigation systems designed, installed, and maintained to keep your landscape healthy while conserving resources.',
  longDescription: [
    'A properly designed irrigation system is essential for maintaining a healthy landscape in Connecticut. Our team designs, installs, and maintains irrigation systems that deliver the right amount of water to each zone of your property, promoting plant health while minimizing water waste.',
    'From new installations for properties in Westport and Darien to repairs and upgrades for existing systems in Fairfield and Norwalk, we handle all aspects of irrigation. We specialize in smart controller technology that adjusts watering based on weather conditions, saving water and money.',
    'Connecticut\'s variable climate requires irrigation systems that can adapt to wet springs, dry summers, and proper winterization before freezing temperatures arrive. Our comprehensive service includes spring startup, seasonal adjustments, and fall winterization to protect your investment.'
  ],
  localContent: {
    intro: 'Connecticut\'s variable rainfall—from wet springs to dry August stretches—makes irrigation essential for maintaining healthy landscapes. Our systems are designed specifically for New England conditions, with proper winterization to prevent freeze damage.',
    regionalBenefits: [
      'Systems designed for Connecticut\'s variable rainfall patterns',
      'Proper winterization to prevent freeze damage',
      'Smart controllers that respond to local weather conditions',
      'Experience with coastal, suburban, and wooded property irrigation needs'
    ],
    seasonalTips: [
      'Spring: System startup, inspection, and adjustment after winter',
      'Summer: Monitoring and adjustment during peak water demand',
      'Fall: Winterization before first freeze to protect pipes and heads',
      'Winter: Indoor controller maintenance and spring planning'
    ]
  },
  image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?q=80&w=2070&auto=format&fit=crop',
  features: [
    'Custom system design and installation',
    'Smart controller technology',
    'Drip irrigation for gardens and beds',
    'Spring startup and inspection',
    'Fall winterization service',
    'Repair and troubleshooting'
  ],
  benefits: [
    'Consistent watering for healthier plants',
    'Water conservation and lower utility bills',
    'Convenience of automated irrigation',
    'Protection of landscape investment',
    'Reduced manual watering labor'
  ],
  process: [
    { title: 'Site Evaluation', description: 'We assess your property, water pressure, and landscape zones to design an optimal system.' },
    { title: 'System Design', description: 'Custom design with proper head placement, zone configuration, and controller selection.' },
    { title: 'Professional Installation', description: 'Expert installation with minimal disruption to existing landscape.' },
    { title: 'Programming & Training', description: 'System setup and walkthrough so you understand how to operate your irrigation.' }
  ],
  relatedServices: [
    { id: 'lawn-maintenance', title: 'Lawn Maintenance' },
    { id: 'landscape-design', title: 'Landscape Design' }
  ],
  faqs: [
    { question: 'How much does an irrigation system cost in Connecticut?', answer: 'Irrigation system costs vary based on property size, zones needed, and features selected. Most residential systems range from $3,000 to $8,000. We provide detailed estimates after site evaluation.' },
    { question: 'Do you service existing irrigation systems?', answer: 'Yes, we service all major brands of irrigation equipment. We offer spring startup, mid-season repairs, fall winterization, and system upgrades.' },
    { question: 'What is irrigation winterization and why is it important?', answer: 'Winterization involves blowing compressed air through the system to remove water before freezing temperatures. This prevents cracked pipes and damaged heads—essential in Connecticut winters.' },
    { question: 'Do you install smart irrigation controllers?', answer: 'Yes, smart controllers are our standard recommendation. They adjust watering based on weather data, saving water and money while maintaining landscape health.' },
    { question: 'How often should irrigation heads be adjusted?', answer: 'We recommend seasonal adjustments as plant growth changes coverage patterns. Our maintenance programs include regular inspection and adjustment.' },
    { question: 'Can you add irrigation to an existing landscape?', answer: 'Absolutely. We install irrigation in established landscapes with minimal disruption using specialized equipment and careful planning.' }
  ],
  targetTowns: ['westport-ct', 'darien-ct', 'fairfield-ct', 'norwalk-ct', 'weston-ct', 'easton-ct', 'trumbull-ct', 'orange-ct'],
  commercialAvailable: true
};

export const enhancedSpringFallCleanupService: EnhancedServiceData = {
  title: 'Spring & Fall Cleanups',
  slug: 'spring-fall-cleanups',
  metaTitle: 'Spring & Fall Cleanup Services CT | Seasonal Landscape Care',
  metaDescription: 'Professional spring and fall cleanup in Fairfield County, CT. Leaf removal, bed preparation, debris clearing & seasonal maintenance. Serving all Connecticut towns.',
  heroHeading: 'Seasonal Cleanup Services for Connecticut Properties',
  description: 'Prepare your property for the changing seasons with thorough cleanup services that address seasonal debris and landscape needs.',
  longDescription: [
    'Connecticut\'s dramatic seasonal changes require dedicated cleanup services to keep your property looking its best. Our spring cleanup prepares your landscape for the growing season, while fall cleanup addresses leaf accumulation and prepares plants for winter dormancy.',
    'In towns like Weston, Easton, and Woodbridge where mature trees create heavy leaf cover, our fall cleanup service is particularly valuable. We provide thorough leaf removal from lawns, beds, and hardscapes, preventing turf damage and creating a tidy appearance.',
    'Spring cleanup addresses winter damage, removes debris, and prepares beds for the growing season. We cut back perennials, edge beds, apply fresh mulch, and ensure your property is ready to flourish as temperatures rise.'
  ],
  localContent: {
    intro: 'Connecticut\'s beautiful deciduous trees create spectacular fall color—and significant leaf volume. Properties in Weston, Easton, Monroe, and Woodbridge face particularly heavy cleanup demands from mature oak, maple, and beech trees.',
    regionalBenefits: [
      'Equipment and crews sized for Connecticut\'s heavy leaf volumes',
      'Multiple-visit programs for extended leaf drop periods',
      'Understanding of Connecticut native perennial care',
      'Proper disposal and composting of organic debris'
    ],
    seasonalTips: [
      'Spring: Cut back perennials, edge beds, remove winter debris, apply mulch',
      'Early Fall: Initial leaf removal as trees begin dropping',
      'Late Fall: Final cleanup after leaf drop complete',
      'Pre-Winter: Protective measures for sensitive plants'
    ]
  },
  image: 'https://images.unsplash.com/photo-1508197149814-0cc02e8b7f74?q=80&w=2069&auto=format&fit=crop',
  features: [
    'Complete leaf removal from lawns and beds',
    'Perennial cutback and bed preparation',
    'Debris and branch removal',
    'Gutter cleaning coordination',
    'Mulch application',
    'Winter protection for sensitive plants'
  ],
  benefits: [
    'Prevents lawn damage from leaf smothering',
    'Prepares landscape for seasonal transitions',
    'Removes hiding spots for pests and disease',
    'Maintains neat, professional appearance',
    'Protects plants through winter'
  ],
  process: [
    { title: 'Initial Assessment', description: 'We evaluate cleanup needs based on tree coverage, property size, and specific requirements.' },
    { title: 'Scheduled Service', description: 'Cleanup scheduled at optimal timing for spring preparation or fall leaf drop.' },
    { title: 'Thorough Cleanup', description: 'Complete debris removal, bed work, and lawn care as needed.' },
    { title: 'Final Inspection', description: 'Walk-through to ensure property is properly prepared for the season ahead.' }
  ],
  relatedServices: [
    { id: 'lawn-maintenance', title: 'Lawn Maintenance' },
    { id: 'mulch-installation', title: 'Mulch Installation' }
  ],
  faqs: [
    { question: 'When should fall cleanup be done in Connecticut?', answer: 'We typically perform fall cleanup in November after most leaves have dropped. For heavy-tree properties, we offer multiple visits throughout October and November.' },
    { question: 'What is included in spring cleanup?', answer: 'Spring cleanup includes debris removal, perennial cutback, bed edging, initial weeding, and often mulch application. We prepare your property for the growing season.' },
    { question: 'How do you handle heavy leaf accumulation?', answer: 'We use commercial equipment including backpack and wheeled blowers, lawn vacuums, and tarps. For large properties, we may use loader equipment for efficient debris removal.' },
    { question: 'Do you remove leaves from beds and gardens?', answer: 'Yes, thorough bed cleanup is part of our service. We remove leaves while being careful around perennials and bulb areas.' },
    { question: 'Is gutter cleaning included?', answer: 'Gutter cleaning can be added to cleanup services. We coordinate with gutter professionals or handle accessible gutters ourselves.' },
    { question: 'How much does seasonal cleanup cost?', answer: 'Cleanup costs depend on property size, tree coverage, and services needed. Most residential cleanups range from $300-$800 per visit. We provide estimates after site evaluation.' }
  ],
  targetTowns: ['weston-ct', 'easton-ct', 'woodbridge-ct', 'monroe-ct', 'newtown-ct', 'bethany-ct', 'trumbull-ct', 'fairfield-ct'],
  commercialAvailable: true
};

export const enhancedMulchService: EnhancedServiceData = {
  title: 'Mulch Installation',
  slug: 'mulch-installation',
  metaTitle: 'Mulch Installation Services CT | Premium Mulch Delivery & Application',
  metaDescription: 'Professional mulch installation in Fairfield County, CT. Premium hardwood, cedar & decorative mulch. Bed preparation & weed prevention. Serving Westport to Shelton.',
  heroHeading: 'Premium Mulch Installation in Connecticut',
  description: 'Enhance your landscape with fresh mulch that improves soil health, reduces weeds, and adds a polished appearance to your property.',
  longDescription: [
    'Fresh mulch transforms the appearance of your landscape while providing essential benefits for plant health. Our mulch installation service includes proper bed preparation, weed prevention measures, and precise application at the correct depth for optimal results.',
    'We offer a variety of mulch options including natural hardwood, cedar, hemlock, and decorative colored mulches. Our team helps you select the right mulch type for your landscape style and plant needs, whether you\'re in coastal Westport or wooded Easton.',
    'Proper mulch application is about more than aesthetics. Correct depth (2-3 inches) retains moisture, regulates soil temperature, and suppresses weeds without suffocating plant roots. We apply mulch correctly and keep it away from tree trunks and plant stems.'
  ],
  localContent: {
    intro: 'Connecticut\'s freeze-thaw cycles make mulch particularly valuable for protecting plant roots. Quality mulch also helps retain moisture during our often-dry late summer periods.',
    regionalBenefits: [
      'Mulch selection suited to Connecticut climate and soil',
      'Proper application depth for New England conditions',
      'Timing coordinated with Connecticut growing season',
      'Local sourcing for fresh, quality materials'
    ],
    seasonalTips: [
      'Spring: Primary mulching season after beds are cleaned and edged',
      'Summer: Touch-up as needed, mulch helps retain moisture',
      'Fall: Additional mulch for winter root protection on sensitive plants',
      'Winter: Mulch insulates roots during freeze-thaw cycles'
    ]
  },
  image: 'https://images.unsplash.com/photo-1629513621928-8888e1c806ed?q=80&w=2070&auto=format&fit=crop',
  features: [
    'Premium mulch varieties',
    'Bed preparation and edging',
    'Weed barrier installation',
    'Precise depth application',
    'Clean application and cleanup',
    'Bulk delivery available'
  ],
  benefits: [
    'Enhanced curb appeal',
    'Weed suppression',
    'Moisture retention',
    'Soil temperature regulation',
    'Improved soil health over time'
  ],
  process: [
    { title: 'Material Selection', description: 'Choose from hardwood, cedar, hemlock, or decorative mulches based on your preferences.' },
    { title: 'Bed Preparation', description: 'We edge beds, remove weeds, and prepare surfaces for mulch application.' },
    { title: 'Professional Application', description: 'Mulch applied at proper depth, kept away from plant stems and tree trunks.' },
    { title: 'Cleanup', description: 'All walkways, driveways, and lawn areas cleaned of any stray mulch.' }
  ],
  relatedServices: [
    { id: 'spring-fall-cleanups', title: 'Spring/Fall Cleanups' },
    { id: 'bed-maintenance', title: 'Bed Maintenance' }
  ],
  faqs: [
    { question: 'What type of mulch is best for Connecticut?', answer: 'Hardwood mulch is our most popular choice—it breaks down slowly, enriches soil, and looks natural. Cedar is excellent for pest resistance. We help you choose based on your landscape and preferences.' },
    { question: 'How deep should mulch be applied?', answer: 'We apply mulch at 2-3 inches depth. Too thin doesn\'t suppress weeds; too thick can suffocate roots and cause problems. Proper depth is essential.' },
    { question: 'How often should mulch be refreshed?', answer: 'Most landscapes benefit from annual mulch application, typically in spring. High-visibility areas may need a mid-season touch-up.' },
    { question: 'Should old mulch be removed before adding new?', answer: 'Usually no—we rake and fluff existing mulch, then add fresh material on top. We only remove old mulch if it\'s excessively deep or decomposed.' },
    { question: 'Do you keep mulch away from tree trunks?', answer: 'Absolutely. "Volcano mulching" against tree trunks causes rot and pest problems. We maintain proper mulch-free zones around all trees and shrubs.' },
    { question: 'Can mulch be delivered in bulk?', answer: 'Yes, we deliver and install bulk mulch for larger properties. This is more economical than bagged mulch for significant coverage areas.' }
  ],
  targetTowns: ['westport-ct', 'darien-ct', 'fairfield-ct', 'weston-ct', 'easton-ct', 'trumbull-ct', 'monroe-ct', 'shelton-ct'],
  commercialAvailable: true
};

export const enhancedFertilizationService: EnhancedServiceData = {
  title: 'Fertilization Programs',
  slug: 'fertilization',
  metaTitle: 'Lawn Fertilization Services CT | Custom Fertilizer Programs',
  metaDescription: 'Professional lawn fertilization in Fairfield County, CT. Custom programs, soil testing, organic options & weed control. Healthier lawns for Connecticut homes.',
  heroHeading: 'Custom Fertilization Programs for Connecticut Lawns',
  description: 'Custom fertilization programs designed to provide your lawn and plants with essential nutrients for optimal growth and health.',
  longDescription: [
    'A healthy lawn requires proper nutrition tailored to your soil conditions and grass type. Our fertilization programs are based on soil testing and designed specifically for Connecticut\'s cool-season grasses and growing conditions.',
    'We offer both traditional and organic fertilization options, applied at optimal times throughout the growing season. Our programs address not just nitrogen needs but also essential micronutrients, pH adjustment, and soil health improvement.',
    'Connecticut soils vary significantly—from sandy coastal areas to clay-heavy inland zones. We analyze your specific conditions and create a program that addresses your lawn\'s unique nutritional needs for thick, healthy turf.'
  ],
  localContent: {
    intro: 'Connecticut\'s acidic soils often require pH correction for optimal lawn health. Our programs address the specific soil conditions found across Fairfield and New Haven Counties.',
    regionalBenefits: [
      'Programs designed for Connecticut cool-season grasses',
      'pH correction for acidic New England soils',
      'Timing optimized for Connecticut growing season',
      'Options for environmentally sensitive coastal areas'
    ],
    seasonalTips: [
      'Early Spring: Light feeding as lawn emerges from dormancy',
      'Late Spring: Primary feeding for vigorous growth',
      'Fall: Critical feeding to build root reserves for winter',
      'Late Fall: Winterizer application before dormancy'
    ]
  },
  image: 'https://images.unsplash.com/photo-1615749188838-38161b5bf02e?q=80&w=2070&auto=format&fit=crop',
  features: [
    'Soil testing and analysis',
    'Custom fertilizer blends',
    'Scheduled seasonal applications',
    'Organic options available',
    'Weed control integration',
    'Grub and pest prevention'
  ],
  benefits: [
    'Thicker, greener lawn',
    'Improved disease resistance',
    'Better drought tolerance',
    'Reduced weed pressure',
    'Long-term soil health'
  ],
  process: [
    { title: 'Soil Testing', description: 'We analyze your soil to understand pH, nutrient levels, and organic matter content.' },
    { title: 'Program Design', description: 'Custom program created based on soil results, grass type, and your goals.' },
    { title: 'Seasonal Applications', description: 'Properly timed applications throughout the growing season.' },
    { title: 'Ongoing Monitoring', description: 'We assess results and adjust the program as your lawn improves.' }
  ],
  relatedServices: [
    { id: 'lawn-maintenance', title: 'Lawn Maintenance' },
    { id: 'aeration', title: 'Lawn Aeration' }
  ],
  faqs: [
    { question: 'How often should lawns be fertilized in Connecticut?', answer: 'Most Connecticut lawns benefit from 4-5 applications per year: early spring, late spring, summer (light), early fall, and late fall winterizer.' },
    { question: 'Do you offer organic fertilization?', answer: 'Yes, we offer organic and hybrid organic programs. These feed your lawn while building long-term soil health through natural processes.' },
    { question: 'Is soil testing necessary?', answer: 'We strongly recommend soil testing for new clients. It reveals pH issues and nutrient deficiencies that generic programs might miss, ensuring more effective treatment.' },
    { question: 'Will fertilizer harm my pets or children?', answer: 'We use products safely and provide guidance on re-entry times. Many of our products are pet-safe once dry. We offer organic options for maximum peace of mind.' },
    { question: 'Does fertilization include weed control?', answer: 'Weed control can be integrated with fertilization. We offer programs that combine feeding and weed prevention for comprehensive lawn care.' },
    { question: 'Why is fall fertilization important?', answer: 'Fall feeding is actually the most important application—it builds root reserves that help your lawn survive winter and emerge strong in spring.' }
  ],
  targetTowns: ['westport-ct', 'darien-ct', 'fairfield-ct', 'norwalk-ct', 'trumbull-ct', 'weston-ct', 'easton-ct', 'monroe-ct'],
  commercialAvailable: true
};

export const enhancedPowerWashingService: EnhancedServiceData = {
  title: 'Power Washing',
  slug: 'power-washing',
  metaTitle: 'Power Washing Services CT | Deck, Patio & Driveway Cleaning',
  metaDescription: 'Professional power washing in Fairfield County, CT. Deck cleaning, patio restoration, driveway cleaning & siding wash. Safe, effective pressure washing.',
  heroHeading: 'Professional Power Washing in Connecticut',
  description: 'Revitalize your outdoor surfaces with professional power washing that safely removes dirt, grime, mold, and stains.',
  longDescription: [
    'Connecticut\'s humid climate promotes mold, mildew, and algae growth on outdoor surfaces. Our professional power washing service safely removes these contaminants along with accumulated dirt, restoring the appearance of your patios, decks, walkways, and more.',
    'We use appropriate pressure levels and techniques for each surface—gentle soft washing for delicate materials and higher pressure for durable concrete and stone. This protects your investment while achieving thorough cleaning.',
    'Regular power washing not only improves appearance but extends the life of outdoor surfaces by removing damaging organic growth. It\'s also essential preparation before staining decks or sealing pavers.'
  ],
  localContent: {
    intro: 'Connecticut\'s humid summers and shaded properties create ideal conditions for mold and mildew growth. Regular power washing removes these growths before they cause permanent staining or surface damage.',
    regionalBenefits: [
      'Experience with Connecticut mold and mildew challenges',
      'Safe techniques for historic and delicate surfaces',
      'Proper disposal of wash water per local regulations',
      'Preparation services for deck staining and paver sealing'
    ],
    seasonalTips: [
      'Spring: Ideal time to clean surfaces after winter',
      'Summer: Remove algae and mildew from humid conditions',
      'Fall: Clean before winter to reduce freeze damage risk',
      'Pre-Event: Clean surfaces before outdoor gatherings'
    ]
  },
  image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=2070&auto=format&fit=crop',
  features: [
    'Deck and patio cleaning',
    'Driveway and walkway washing',
    'Fence brightening',
    'Siding and exterior cleaning',
    'Pre-stain deck preparation',
    'Paver cleaning and sealing prep'
  ],
  benefits: [
    'Restored surface appearance',
    'Extended surface lifespan',
    'Improved safety (removes slippery algae)',
    'Enhanced curb appeal',
    'Preparation for staining or sealing'
  ],
  process: [
    { title: 'Surface Assessment', description: 'We evaluate surfaces to determine appropriate pressure and cleaning methods.' },
    { title: 'Pre-Treatment', description: 'Application of cleaning solutions to break down organic growth and stains.' },
    { title: 'Power Washing', description: 'Careful washing at appropriate pressure levels for each surface type.' },
    { title: 'Final Rinse & Inspection', description: 'Thorough rinsing and walk-through to ensure complete cleaning.' }
  ],
  relatedServices: [
    { id: 'hardscape-projects', title: 'Hardscape Projects' },
    { id: 'fence-work', title: 'Fence Work' }
  ],
  faqs: [
    { question: 'Is power washing safe for my deck?', answer: 'Yes, when done correctly. We use appropriate pressure levels and fan tips for wood surfaces. High pressure can damage wood, so technique matters—that\'s why professional service is important.' },
    { question: 'How often should outdoor surfaces be power washed?', answer: 'Most surfaces benefit from annual cleaning. Shaded areas or surfaces with heavy organic growth may need more frequent attention.' },
    { question: 'Can you remove oil stains from driveways?', answer: 'We can significantly improve oil stains using degreasers and hot water. Very old or deep stains may lighten but not completely disappear.' },
    { question: 'Do you offer soft washing for siding?', answer: 'Yes, soft washing uses lower pressure with cleaning solutions—ideal for vinyl siding, stucco, and other surfaces that could be damaged by high pressure.' },
    { question: 'Should I power wash before staining my deck?', answer: 'Absolutely. Power washing removes dirt, old finish, and mold that would prevent stain adhesion. It\'s essential preparation for a lasting stain job.' },
    { question: 'What areas do you serve for power washing?', answer: 'We provide power washing services throughout Fairfield and New Haven Counties, including Westport, Darien, Fairfield, Trumbull, and surrounding towns.' }
  ],
  targetTowns: ['westport-ct', 'darien-ct', 'fairfield-ct', 'norwalk-ct', 'trumbull-ct', 'stratford-ct', 'milford-ct', 'shelton-ct'],
  commercialAvailable: true
};

export const enhancedBedMaintenanceService: EnhancedServiceData = {
  title: 'Bed Maintenance',
  slug: 'bed-maintenance',
  metaTitle: 'Garden Bed Maintenance CT | Weeding, Edging & Plant Care',
  metaDescription: 'Professional garden bed maintenance in Fairfield County, CT. Weeding, edging, pruning & seasonal flower care. Keep your landscape beds looking pristine.',
  heroHeading: 'Expert Garden Bed Maintenance in Connecticut',
  description: 'Keep your garden beds looking pristine with thorough maintenance services that enhance your landscape\'s overall appearance.',
  longDescription: [
    'Beautiful garden beds require regular attention to maintain their appearance and plant health. Our bed maintenance service includes weeding, edging, pruning, deadheading, and monitoring plant health throughout the growing season.',
    'We understand Connecticut\'s unique plant palette—from native perennials to specimen shrubs—and provide care tailored to each plant\'s needs. Whether you have formal gardens in Darien or naturalistic plantings in Weston, we maintain your beds to the highest standards.',
    'Regular bed maintenance prevents small problems from becoming major issues. We catch pest infestations early, address disease symptoms promptly, and ensure your investment in plants continues to thrive and mature beautifully.'
  ],
  localContent: {
    intro: 'Connecticut gardens feature diverse plantings from native woodland species to formal hedging. Each requires specific care throughout our distinct growing seasons.',
    regionalBenefits: [
      'Knowledge of Connecticut-native and adapted plant care',
      'Experience with deer-resistant landscape maintenance',
      'Understanding of coastal and inland growing conditions',
      'Proper timing for Connecticut growing season'
    ],
    seasonalTips: [
      'Spring: Cutback, division, early weeding, and mulching',
      'Summer: Regular weeding, deadheading, and pest monitoring',
      'Fall: Cleanup, cutback of spent perennials, bulb planting',
      'Winter: Protection for tender plants, structural pruning'
    ]
  },
  image: 'https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e?q=80&w=2071&auto=format&fit=crop',
  features: [
    'Regular weeding and cultivation',
    'Edge maintenance and definition',
    'Deadheading and plant grooming',
    'Seasonal plant care',
    'Pest and disease monitoring',
    'Annual and perennial management'
  ],
  benefits: [
    'Pristine bed appearance',
    'Healthier, more vigorous plants',
    'Reduced weed competition',
    'Early problem detection',
    'Extended bloom periods'
  ],
  process: [
    { title: 'Initial Assessment', description: 'We evaluate your beds, identify plant varieties, and note any issues or special care needs.' },
    { title: 'Regular Service', description: 'Scheduled visits for weeding, grooming, and maintenance tasks.' },
    { title: 'Seasonal Care', description: 'Specific tasks performed at optimal times throughout the year.' },
    { title: 'Ongoing Monitoring', description: 'We watch for pest, disease, or cultural problems and address them promptly.' }
  ],
  relatedServices: [
    { id: 'mulch-installation', title: 'Mulch Installation' },
    { id: 'pruning-weeding', title: 'Pruning & Weeding' }
  ],
  faqs: [
    { question: 'How often should garden beds be maintained?', answer: 'For best appearance, beds should be visited every 2-4 weeks during the growing season. Frequency depends on planting style—formal gardens need more attention than naturalistic designs.' },
    { question: 'Do you handle perennial division?', answer: 'Yes, we divide overgrown perennials at the appropriate time for each species, improving plant health and providing material for expanding gardens.' },
    { question: 'Can you identify mystery plants in my beds?', answer: 'Usually yes! Our trained horticulturists can identify most plants and provide appropriate care recommendations.' },
    { question: 'What about deer damage in my garden?', answer: 'We work with deer-resistant plant selection and can recommend deterrent strategies. For clients in Easton, Weston, and other deer-heavy areas, this is an important consideration.' },
    { question: 'Is bed maintenance included in lawn care contracts?', answer: 'Bed maintenance is typically a separate service from lawn care. Many clients combine both for comprehensive property care.' },
    { question: 'Do you plant annuals and seasonal color?', answer: 'Yes, we design and install annual plantings for seasonal color. This is a popular enhancement for high-visibility areas.' }
  ],
  targetTowns: ['westport-ct', 'darien-ct', 'weston-ct', 'easton-ct', 'fairfield-ct', 'woodbridge-ct', 'orange-ct', 'trumbull-ct'],
  commercialAvailable: true
};

// Map enhanced services by slug
export const enhancedServiceData: Record<string, EnhancedServiceData> = {
  'lawn-maintenance': enhancedLawnMaintenanceService,
  'landscape-design': enhancedLandscapeDesignService,
  'hardscaping': enhancedHardscapeService,
  'hardscape-projects': enhancedHardscapeService,
  'snow-removal': enhancedSnowRemovalService,
  'specialty-services': enhancedSnowRemovalService,
  'irrigation': enhancedIrrigationService,
  'spring-fall-cleanups': enhancedSpringFallCleanupService,
  'mulch-installation': enhancedMulchService,
  'fertilization': enhancedFertilizationService,
  'power-washing': enhancedPowerWashingService,
  'bed-maintenance': enhancedBedMaintenanceService
};
